/** Group sender-key AEAD helpers — SPEC §13.1 */

function b64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function unb64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function asBuf(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

export function generateGroupSenderKey(): string {
  return b64(crypto.getRandomValues(new Uint8Array(32)));
}

export async function encryptGroupText(
  senderKeyB64: string,
  plaintext: string,
  aad: string,
): Promise<string> {
  const keyBytes = unb64(senderKeyB64);
  const key = await crypto.subtle.importKey("raw", asBuf(keyBytes), "AES-GCM", false, ["encrypt"]);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv, additionalData: new TextEncoder().encode(aad) },
      key,
      new TextEncoder().encode(plaintext),
    ),
  );
  const packed = new Uint8Array(iv.length + ct.length);
  packed.set(iv, 0);
  packed.set(ct, iv.length);
  return b64(packed);
}

export async function decryptGroupText(
  senderKeyB64: string,
  ctB64: string,
  aad: string,
): Promise<string> {
  const keyBytes = unb64(senderKeyB64);
  const packed = unb64(ctB64);
  const iv = packed.subarray(0, 12);
  const ct = packed.subarray(12);
  const key = await crypto.subtle.importKey("raw", asBuf(keyBytes), "AES-GCM", false, ["decrypt"]);
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: asBuf(iv), additionalData: new TextEncoder().encode(aad) },
    key,
    asBuf(ct),
  );
  return new TextDecoder().decode(pt);
}
